import React from "react";
import { motion } from "framer-motion";
import { Check, Loader2 } from "lucide-react";
import { GI_SETS, PRODUCT_COPY } from "@/lib/brand";
import { Reveal, SectionLabel } from "@/components/Motion";
import { GiStrip, GiPoster } from "@/components/GiMedia";
import { useStore } from "@/context/StoreContext";
import { useCheckout } from "@/hooks/useCheckout";

const TECHNICAL = [
  "Drag and drop — just ensure orvyn_karate_gis",
  "No scripts and no framework required",
  "Own clothing collection, so no conflicts with other add-on clothing",
  "Works with any clothing menu that supports add-on collections",
  "README includes slot numbers and matching colour combinations",
];

const Column = ({ label, model, items, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 26 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] }}
    data-testid={`gi-column-${label.toLowerCase()}`}
    className="border border-white/10 p-7"
  >
    <div className="flex items-baseline justify-between gap-4">
      <h3 className="font-display text-xl font-bold uppercase tracking-tight text-white">{label}</h3>
      <code className="font-mono text-[10px] tracking-[0.06em] text-white/30">{model}</code>
    </div>
    <ul className="mt-6 divide-y divide-white/6">
      {items.map((item, i) => (
        <li key={item} className="flex items-center gap-4 py-3.5">
          <span className="grid h-6 w-6 shrink-0 place-items-center border border-white/12 font-mono text-[10px] text-white/40">
            {String(i + 1).padStart(2, "0")}
          </span>
          <span className="flex-1 text-[13px] text-white/65">{item}</span>
          <span className="font-mono text-[9px] uppercase tracking-[0.14em] text-white/25">
            Jacket + trousers
          </span>
        </li>
      ))}
    </ul>
  </motion.div>
);

export const KarateGis = () => {
  const { products, format } = useStore();
  const { buy, pending } = useCheckout();
  const addon = products.find((p) => p.slug === "karate-gi");
  const copy = PRODUCT_COPY["karate-gi"];
  if (!addon) return null;
  const busy = pending === "karate-gi";

  return (
    <section id="karate-gis" className="relative border-b border-white/8 py-24 lg:py-36">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="grid gap-10 pb-12 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-7">
            <SectionLabel index="§ 04">Orvyn Karate Female and Male GI's</SectionLabel>
            <Reveal delay={0.05}>
              <h2 className="mt-6 font-display text-[clamp(2rem,4.6vw,3.6rem)] font-extrabold uppercase leading-[0.96] tracking-[-0.015em] text-white">
                Eight gis.
                <br />
                <span className="text-white/35">Zero conflicts.</span>
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-5 lg:pb-3">
            <Reveal delay={0.12}>
              <p className="text-sm leading-relaxed text-white/45">{copy.blurb}</p>
            </Reveal>
          </div>
        </div>

        <Reveal y={36} className="pb-5">
          <GiPoster />
          <div className="mt-5">
            <GiStrip />
          </div>
          <p className="mt-3 text-center font-mono text-[10px] uppercase tracking-[0.16em] text-white/25">
            Female colours shown individually · full male pack above
          </p>
        </Reveal>

        <div className="grid gap-5 lg:grid-cols-12">
          <div className="grid gap-5 sm:grid-cols-2 lg:col-span-8">
            <Column label="Female" model={GI_SETS.female.model} items={GI_SETS.female.items} delay={0} />
            <Column label="Male" model={GI_SETS.male.model} items={GI_SETS.male.items} delay={0.08} />
          </div>

          <Reveal y={30} delay={0.14} className="lg:col-span-4">
            <div className="grain flex h-full flex-col border border-white/12 bg-[#0b0b0e] p-7">
              <div className="label">Technical</div>
              <ul className="mt-6 space-y-3.5">
                {TECHNICAL.map((t) => (
                  <li key={t} className="flex items-start gap-3 text-[13px] leading-relaxed text-white/55">
                    <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[hsl(var(--orv-blue))]" />
                    {t}
                  </li>
                ))}
              </ul>

              <div className="mt-auto pt-8">
                <div className="flex items-end justify-between gap-4">
                  <span className="label">One-time</span>
                  <span className="font-display text-3xl font-extrabold tracking-tight text-white">
                    {format(addon.price)}
                  </span>
                </div>
                <button
                  onClick={() => buy("karate-gi")}
                  data-testid="gi-section-buy-button"
                  className="group relative mt-5 w-full overflow-hidden bg-white py-3.5 font-mono text-[11px] uppercase tracking-[0.18em] text-black"
                >
                  <span className="relative z-10 flex items-center justify-center gap-2.5">
                    {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                    {busy ? "Opening checkout…" : addon.available ? "Buy the gis" : "Notify me on Discord"}
                  </span>
                  <span className="absolute inset-0 z-0 -translate-y-full bg-[hsl(var(--orv-blue))] transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:translate-y-0" />
                </button>
                <p className="mt-4 text-[12px] leading-relaxed text-white/30">{copy.pairs}</p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
